import { DropdownNavList, IconButton, SkeletonButton, Space } from '@billy/design-system'
import { ReactElement, useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { useUserOrganization } from '@modules-deprecated/app/organization'
import { useUmbrella } from '@modules-deprecated/app/umbrellas'
import { ExpensesTrackingEvents } from '@views/receipts/enums/tracking'

import { EmberRoute } from '../../../../enums/emberRoute'
import { useOrganizationSource, useSegment } from '../../../../hooks'
import { useIsAdvisorExpertLayer } from '../AdvisorLayer/hooks/useIsAdvisorExpertLayer'
import { useFastMenuItems } from './hooks/useFastMenuItems'
import * as Styled from './styles'
import { MenuNavItemValue } from './types/menuNavItem'
import { getFastMenuEventActionFromRoute } from './utils/getFastMenuEventActionFromRoute'

const FAST_MENU_DROPDOWN_WIDTH = 195

export const MenuActions = (): ReactElement | null => {
  const { track } = useSegment()
  const { isLoading: isUmbrellaLoading } = useUmbrella()
  const { isLoading: isLoadingOrganization, organization } = useUserOrganization()
  const { items: fastMenuItems, isLoading: isFastMenuItemsLoading } = useFastMenuItems()
  const { isSourceZervant } = useOrganizationSource()
  const { t } = useTranslation()
  const isExpertLayer = useIsAdvisorExpertLayer()

  const isLoading = (isLoadingOrganization && isUmbrellaLoading) || isFastMenuItemsLoading
  const isOrganizationLocked = organization?.isLocked
  const hasFastMenuItems = fastMenuItems.length > 0

  const trackFastMenuClick = useCallback(
    (route: MenuNavItemValue, id: string) => {
      const basePayload = {
        context: 'menu',
        action: getFastMenuEventActionFromRoute(route),
      }

      if (id === 'new-bill') {
        track(ExpensesTrackingEvents.CreatePurchaseClicked, {
          source: 'Quick Action',
        })
      }

      if (isSourceZervant && route === EmberRoute.InvoicesNew) {
        track('XXX - Invoicing Core - Menu Fast Items - Create new invoice clicked', basePayload)
      }

      if (isSourceZervant && route === EmberRoute.QuotesNew) {
        track('XXX - Invoicing Core - Menu Fast Items - Create new quote clicked', basePayload)
      }

      // Specific legacy / experimental event (kept for backward compatibility)
      track('XXX - Jesper - Action Taken', basePayload)
    },
    [isSourceZervant, track],
  )

  const handleItemSelect = useCallback(
    (id: string, route: MenuNavItemValue) => {
      trackFastMenuClick(route, id)
    },
    [trackFastMenuClick],
  )

  const CreateButton = useMemo(
    () => (
      <Styled.IconButtonWrapper>
        <IconButton shape="pill" size="md" variant="outlined" icon="plus" aria-label={t('menu.create_new')} />
      </Styled.IconButtonWrapper>
    ),
    [t],
  )

  if (!isLoading && isOrganizationLocked) {
    return null
  }

  if (isExpertLayer) {
    return null
  }

  return (
    <Styled.MenuActionsWrapper>
      {isLoading ? (
        <SkeletonButton size="lg" fullWidth />
      ) : (
        hasFastMenuItems && (
          <>
            <DropdownNavList
              items={fastMenuItems}
              onItemClick={handleItemSelect}
              maxHeight="default"
              placement="bottom-start"
              scrollEndOffset={60}
              size="xl"
              subItemsMode="vertical"
              trigger={CreateButton}
              width={FAST_MENU_DROPDOWN_WIDTH}
            />
            <Space size="s8" />
          </>
        )
      )}
    </Styled.MenuActionsWrapper>
  )
}
